import React, { useState, useEffect, useRef } from 'react';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  PhoneOff,
  Monitor,
  Maximize2,
  Minimize2,
  MessageSquare,
  Radio,
  X,
  Send,
  Paperclip,
  Smile,
  Reply,
  CheckSquare,
  FileText,
  Download,
  PictureInPicture2,
  Loader2,
  Copy,
  Check,
  Trash2,
  Forward,
} from 'lucide-react';
import { useCall } from '../../context/CallContext.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';
import { ChatMessage, ChatAttachment, ChatChannel } from '../../../shared/types.ts';
import { ForwardMessageModal } from '../chat/ForwardMessageModal.tsx';
import { Avatar } from '../common/Avatar.tsx';
import { LinkifiedText } from '../common/LinkifiedText.tsx';
import { EmojiPickerPopover } from '../chat/EmojiPickerPopover.tsx';
import { VoiceNotePlayer } from '../chat/VoiceNotePlayer.tsx';
import { CreateTaskModal } from '../tasks/CreateTaskModal.tsx';
import { playIncomingMessageSound, playOutgoingMessageSound } from '../../services/soundEffects.ts';

// Interactive component to bind MediaStream to an HTML5 video tag
export const VideoFeed: React.FC<{
  stream: MediaStream | null;
  isMuted?: boolean;
  className?: string;
  videoRefCallback?: (el: HTMLVideoElement | null) => void;
}> = ({ stream, isMuted = false, className = '', videoRefCallback }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) return;

    video.srcObject = stream;
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        console.warn('[VideoFeed] Playback catch (expected when autoplay policy engages):', err);
      });
    }

    const handleTrackChange = () => {
      if (video) {
        video.srcObject = stream;
        video.play().catch(() => {});
      }
    };

    stream.addEventListener('addtrack', handleTrackChange);
    stream.addEventListener('removetrack', handleTrackChange);

    return () => {
      stream.removeEventListener('addtrack', handleTrackChange);
      stream.removeEventListener('removetrack', handleTrackChange);
    };
  }, [stream]);

  if (!stream) return null;

  return (
    <video
      ref={(el) => {
        videoRef.current = el;
        if (videoRefCallback) videoRefCallback(el);
      }}
      autoPlay
      playsInline
      muted={isMuted}
      className={className}
    />
  );
};

// Dedicated background audio receiver component ensuring continuous bidirectional sound
export const RemoteAudioPlayer: React.FC<{ stream: MediaStream }> = ({ stream }) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !stream) return;

    audio.srcObject = stream;
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        console.warn('[RemoteAudioPlayer] Autoplay prevented:', err);
      });
    }

    const handleTrackChange = () => {
      if (audio) {
        audio.srcObject = stream;
        audio.play().catch(() => {});
      }
    };

    stream.addEventListener('addtrack', handleTrackChange);
    stream.addEventListener('removetrack', handleTrackChange);

    return () => {
      stream.removeEventListener('addtrack', handleTrackChange);
      stream.removeEventListener('removetrack', handleTrackChange);
    };
  }, [stream]);

  return <audio ref={audioRef} autoPlay playsInline />;
};

const isImageFile = (fileName?: string, mimeType?: string): boolean => {
  if (mimeType && mimeType.startsWith('image/')) return true;
  if (!fileName) return false;
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(fileName);
};

const formatCallMsgTime = (dateStr?: string | null): string => {
  if (!dateStr) return '';
  try {
    const sanitized =
      dateStr.includes(' ') && !dateStr.includes('T') ? dateStr.replace(' ', 'T') : dateStr;
    const d = new Date(sanitized);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
};

export const VideoCallModal: React.FC = () => {
  const {
    activeCall,
    localStream,
    screenStream,
    remoteStreams,
    isOutgoingRinging,
    endCall,
    toggleAudio,
    toggleVideo,
    toggleScreenShare,
    toggleFloating,
  } = useCall();
  const { user, token } = useAuth();
  const { subscribe, joinChannel, leaveChannel } = useRealtime();

  const [callDuration, setCallDuration] = useState(0);
  const [showInCallChat, setShowInCallChat] = useState(false);
  const [unreadInCallCount, setUnreadInCallCount] = useState(0);

  // In-Call Live Chat & File Sharing state
  const [resolvedChannelId, setResolvedChannelId] = useState<string | null>(null);
  const [channelProjectId, setChannelProjectId] = useState<string | undefined>(undefined);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [stagedAttachments, setStagedAttachments] = useState<ChatAttachment[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isSendingChat, setIsSendingChat] = useState(false);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const [hoveredMsgId, setHoveredMsgId] = useState<string | null>(null);

  // Forward a call-chat message to any other chat
  const [forwardingMessage, setForwardingMessage] = useState<ChatMessage | null>(null);
  const [forwardChannels, setForwardChannels] = useState<ChatChannel[]>([]);

  const openForwardMessage = async (msg: ChatMessage) => {
    setForwardingMessage(msg);
    try {
      const res = await fetch('/api/chat/channels', {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setForwardChannels(data.channels || []);
      }
    } catch (err) {
      console.error('[VideoCallModal] Could not load chats to forward to:', err);
    }
  };

  const handleForwardMessage = async (targetChannelId: string) => {
    if (!forwardingMessage) return;
    const res = await fetch(`/api/chat/messages/${forwardingMessage.id}/forward`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
      },
      body: JSON.stringify({ targetChannelId }),
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      let serverMessage = '';
      try {
        serverMessage = JSON.parse(errText).error || '';
      } catch {
        // not JSON — fall back to the HTTP status below
      }
      alert(serverMessage || `Could not forward the message (server responded ${res.status}).`);
      throw new Error('forward failed');
    }
    const data = await res.json();
    // Forwarded into the meeting chat itself: show it right away
    if (data.channelId === resolvedChannelId && data.message) {
      setMessages((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]));
    }
    setForwardingMessage(null);
  };
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null);

  // Voice Note Recording in Call Chat
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const voiceTimerRef = useRef<any>(null);

  // Task creation inside call chat
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  const [taskInitialTitle, setTaskInitialTitle] = useState('');

  // Desktop OS Picture-in-Picture (popup beyond browser)
  const [isDesktopPiPActive, setIsDesktopPiPActive] = useState(false);
  const pipCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pipVideoRef = useRef<HTMLVideoElement | null>(null);
  const activeStageVideoElRef = useRef<HTMLVideoElement | null>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Call timer
  useEffect(() => {
    if (!activeCall) return;
    const interval = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
    return () => {
      clearInterval(interval);
      setCallDuration(0);
    };
  }, [activeCall?.callId]);

  // Resolve chat channel for this call so in-call chat & file sharing works for all calls
  useEffect(() => {
    if (!activeCall || !token) {
      setResolvedChannelId(null);
      setMessages([]);
      return;
    }

    let cancelled = false;

    const initCallChannel = async () => {
      let targetChannelId = activeCall.channelId || null;

      if (!targetChannelId) {
        // Find remote participant to resolve direct channel
        const myId = user?.id || '';
        const other = activeCall.participants.find((p) => p.id !== myId && p.id !== 'me');
        if (other) {
          try {
            const res = await fetch('/api/chat/channels', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
              },
              body: JSON.stringify({
                type: 'direct',
                targetUserId: other.id,
              }),
            });
            if (res.ok) {
              const data = await res.json();
              targetChannelId = data.channel?.id || data.channelId || null;
            }
          } catch (err) {
            console.warn('[VideoCallModal] Could not resolve direct channel:', err);
          }
        }
      }

      if (cancelled || !targetChannelId) return;
      setResolvedChannelId(targetChannelId);
      joinChannel(targetChannelId);

      // Fetch channel info (to check if it belongs to a project) and messages
      try {
        const [channelsRes, msgsRes] = await Promise.all([
          fetch('/api/chat/channels', {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
            },
          }),
          fetch(`/api/chat/channels/${targetChannelId}/messages`, {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
            },
          }),
        ]);

        if (!cancelled && channelsRes.ok) {
          const chData = await channelsRes.json();
          const match = (chData.channels || []).find((c: any) => c.id === targetChannelId);
          if (match?.projectId) {
            setChannelProjectId(match.projectId);
          } else {
            setChannelProjectId(undefined);
          }
        }

        if (!cancelled && msgsRes.ok) {
          const msgData = await msgsRes.json();
          setMessages(msgData.messages || []);
          setTimeout(() => {
            chatEndRef.current?.scrollIntoView({ behavior: 'auto' });
          }, 60);
        }
      } catch (err) {
        console.error('[VideoCallModal] Failed to load call chat messages:', err);
      }
    };

    initCallChannel();

    return () => {
      cancelled = true;
    };
  }, [activeCall?.callId, activeCall?.channelId, activeCall?.participants.length, token, user?.id]);

  // Subscribe to live messages in the call's chat channel
  useEffect(() => {
    if (!resolvedChannelId) return;

    const unsubscribe = subscribe((event) => {
      // The server sends chat events as { type, data } (same shape ChatWorkspace consumes)
      const eventData: any = (event as any).data;
      if (event.type === 'chat_message' && eventData?.channelId === resolvedChannelId) {
        const incomingMsg: ChatMessage = eventData;
        if (!incomingMsg?.id) return;

        setMessages((prev) => {
          if (prev.some((m) => m.id === incomingMsg.id)) return prev;
          return [...prev, incomingMsg];
        });

        if (incomingMsg.senderId !== user?.id) {
          if (!showInCallChat) {
            setUnreadInCallCount((prev) => prev + 1);
          }
          playIncomingMessageSound();
        }

        setTimeout(() => {
          chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 60);
      } else if (event.type === 'message_reaction' && eventData?.channelId === resolvedChannelId) {
        const { messageId, reactions } = eventData;
        setMessages((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, reactions } : m))
        );
      }
    });

    return () => {
      unsubscribe();
    };
  }, [resolvedChannelId, showInCallChat, subscribe, user?.id]);

  // Clear unread badge when opening in-call chat
  useEffect(() => {
    if (showInCallChat) {
      setUnreadInCallCount(0);
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 60);
    }
  }, [showInCallChat]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Desktop OS Picture-in-Picture Canvas Compositor (runs during active call so PiP window works for both audio & video calls beyond browser)
  useEffect(() => {
    if (!activeCall) {
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture().catch(() => {});
      }
      setIsDesktopPiPActive(false);
      return;
    }

    const canvas = pipCanvasRef.current;
    const pipVideo = pipVideoRef.current;
    if (!canvas || !pipVideo) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animTimer: any = null;

    const renderFrame = () => {
      // Background
      ctx.fillStyle = '#121417';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const videoEl = activeStageVideoElRef.current;
      if (videoEl && videoEl.readyState >= 2 && videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
        // Fit video aspect ratio inside 640x360 canvas
        const scale = Math.min(
          canvas.width / videoEl.videoWidth,
          canvas.height / videoEl.videoHeight
        );
        const drawW = videoEl.videoWidth * scale;
        const drawH = videoEl.videoHeight * scale;
        const drawX = (canvas.width - drawW) / 2;
        const drawY = (canvas.height - drawH) / 2;
        ctx.drawImage(videoEl, drawX, drawY, drawW, drawH);
      } else {
        // Audio call / camera off visual card for Desktop PiP popup
        const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        grad.addColorStop(0, '#0f172a');
        grad.addColorStop(1, '#1e1b4b');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const myId = user?.id || '';
        const mainPeer =
          activeCall.participants.find((p) => p.id !== myId && p.id !== 'me') ||
          activeCall.participants[0];
        const initials = (mainPeer?.name || activeCall.title || 'Call')
          .slice(0, 2)
          .toUpperCase();

        // Avatar circle
        ctx.beginPath();
        ctx.arc(canvas.width / 2, canvas.height / 2 - 24, 48, 0, Math.PI * 2);
        ctx.fillStyle = '#2563eb';
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(255,255,255,0.25)';
        ctx.stroke();

        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 32px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(initials, canvas.width / 2, canvas.height / 2 - 24);

        // Call Title
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 20px sans-serif';
        ctx.fillText(
          mainPeer?.name || activeCall.title,
          canvas.width / 2,
          canvas.height / 2 + 52
        );

        // Role / Status
        ctx.fillStyle = '#94a3b8';
        ctx.font = '14px sans-serif';
        ctx.fillText(
          isOutgoingRinging ? 'Ringing...' : mainPeer?.role || 'Active Call',
          canvas.width / 2,
          canvas.height / 2 + 78
        );
      }

      // Top overlay bar with call timer
      ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
      ctx.fillRect(0, 0, canvas.width, 36);
      ctx.fillStyle = '#4ade80';
      ctx.beginPath();
      ctx.arc(20, 18, 5, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 13px monospace';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(
        `${activeCall.title}  •  ${ isOutgoingRinging ? 'Ringing...' : formatTime(callDuration)}`,
        34,
        18
      );
    };

    renderFrame();
    animTimer = setInterval(renderFrame, 100);

    try {
      if (!pipVideo.srcObject && typeof (canvas as any).captureStream === 'function') {
        const stream = (canvas as any).captureStream(15);
        pipVideo.srcObject = stream;
        pipVideo.play().catch(() => {});
      }
      // Enable automatic Picture-in-Picture when browser is minimized / tab switched on supported browsers
      if ('autoPictureInPicture' in pipVideo) {
        (pipVideo as any).autoPictureInPicture = true;
      }
    } catch (err) {
      console.warn('[VideoCallModal] PiP stream init warning:', err);
    }

    const handleEnterPiP = () => setIsDesktopPiPActive(true);
    const handleLeavePiP = () => setIsDesktopPiPActive(false);

    pipVideo.addEventListener('enterpictureinpicture', handleEnterPiP);
    pipVideo.addEventListener('leavepictureinpicture', handleLeavePiP);

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'hidden' &&
        document.pictureInPictureEnabled &&
        !document.pictureInPictureElement &&
        pipVideo.readyState >= 2
      ) {
        pipVideo.requestPictureInPicture().catch(() => {
          // Browsers without prior user gesture or autoPiP permission may ignore automatic trigger
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(animTimer);
      pipVideo.removeEventListener('enterpictureinpicture', handleEnterPiP);
      pipVideo.removeEventListener('leavepictureinpicture', handleLeavePiP);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeCall?.callId, callDuration, isOutgoingRinging, activeCall?.participants, user?.id]);

  const handleToggleDesktopPiP = async () => {
    const pipVideo = pipVideoRef.current;
    if (!pipVideo) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsDesktopPiPActive(false);
      } else if (document.pictureInPictureEnabled) {
        await pipVideo.play().catch(() => {});
        await pipVideo.requestPictureInPicture();
        setIsDesktopPiPActive(true);
      }
    } catch (err) {
      console.warn('[VideoCallModal] Failed to toggle Desktop PiP:', err);
    }
  };

  // Upload files in call chat
  const uploadInCallFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0 || !token) return;

    setIsUploadingFile(true);
    try {
      for (const file of fileArray) {
        const formData = new FormData();
        formData.append('file', file);
        if (resolvedChannelId) {
          formData.append('channelId', resolvedChannelId);
        }

        const res = await fetch('/api/files/upload', {
          method: 'POST',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
          body: formData,
        });

        if (res.ok) {
          const data = await res.json();
          const rec = data.file;
          const isImg = isImageFile(rec.originalName, rec.mimeType);
          setStagedAttachments((prev) => [
            ...prev,
            {
              fileId: rec.id,
              fileName: rec.originalName,
              fileSize: rec.fileSize,
              mimeType: rec.mimeType,
              url: isImg
                ? rec.previewUrl || `/api/files/${rec.id}/preview`
                : rec.downloadUrl || `/api/files/${rec.id}/download`,
            },
          ]);
        }
      }
    } catch (err) {
      console.error('[VideoCallModal] File upload error:', err);
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSendInCallMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (
      (!chatInput.trim() && stagedAttachments.length === 0) ||
      !resolvedChannelId ||
      !token ||
      isSendingChat
    ) {
      return;
    }

    setIsSendingChat(true);
    const contentToSend =
      chatInput.trim() ||
      (stagedAttachments.length === 1 &&
      isImageFile(stagedAttachments[0].fileName, stagedAttachments[0].mimeType)
        ? 'Photo'
        : `Shared ${stagedAttachments.length} file(s)`);
    const attachmentsToSend = [...stagedAttachments];
    const replyToId = replyingTo?.id;
    const originalInput = chatInput;

    setChatInput('');
    setStagedAttachments([]);
    setReplyingTo(null);

    try {
      const res = await fetch(`/api/chat/channels/${resolvedChannelId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          content: contentToSend,
          attachments: attachmentsToSend,
          replyToId,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setMessages((prev) => {
          if (prev.some((m) => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
        playOutgoingMessageSound();
        setTimeout(() => {
          chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
      } else {
        // Give the text and attachments back so nothing typed is lost, and say why it failed
        const errText = await res.text().catch(() => '');
        let serverMessage = '';
        try {
          serverMessage = JSON.parse(errText).error || '';
        } catch {
          // not JSON — fall back to the HTTP status below
        }
        setChatInput(originalInput);
        setStagedAttachments(attachmentsToSend);
        alert(serverMessage || `Could not send the message (server responded ${res.status}).`);
      }
    } catch (err) {
      console.error('[VideoCallModal] Failed to send in-call message:', err);
      setChatInput(originalInput);
      setStagedAttachments(attachmentsToSend);
    } finally {
      setIsSendingChat(false);
    }
  };

  const handleToggleReaction = async (messageId: string, emoji: string) => {
    if (!token || !user) return;
    try {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m;
          const currentReactions = m.reactions || [];
          const existing = currentReactions.find((r) => r.emoji === emoji);
          const hasMine = existing?.userIds.includes(user.id);
          let nextReactions;
          if (existing) {
            if (hasMine) {
              const nextIds = existing.userIds.filter((id) => id !== user.id);
              nextReactions =
                nextIds.length === 0
                  ? currentReactions.filter((r) => r.emoji !== emoji)
                  : currentReactions.map((r) =>
                      r.emoji === emoji ? { ...r, count: nextIds.length, userIds: nextIds } : r
                    );
            } else {
              const nextIds = [...existing.userIds, user.id];
              nextReactions = currentReactions.map((r) =>
                r.emoji === emoji ? { ...r, count: nextIds.length, userIds: nextIds } : r
              );
            }
          } else {
            nextReactions = [...currentReactions, { emoji, count: 1, userIds: [user.id] }];
          }
          return { ...m, reactions: nextReactions };
        })
      );

      await fetch(`/api/chat/messages/${messageId}/reactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({ emoji }),
      });
    } catch (err) {
      console.error('[VideoCallModal] Reaction error:', err);
    }
  };

  const startVoiceNoteRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.start(100);
      setIsRecordingVoice(true);
      setRecordingSeconds(0);
      voiceTimerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('[VideoCallModal] Voice note mic error:', err);
    }
  };

  const cancelVoiceNoteRecording = () => {
    if (voiceTimerRef.current) clearInterval(voiceTimerRef.current);
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    audioChunksRef.current = [];
    setIsRecordingVoice(false);
    setRecordingSeconds(0);
  };

  const sendVoiceNoteRecording = async () => {
    if (!mediaRecorderRef.current || !resolvedChannelId || !token) return;
    if (voiceTimerRef.current) clearInterval(voiceTimerRef.current);
    const duration = recordingSeconds;
    const recorder = mediaRecorderRef.current;

    recorder.onstop = async () => {
      try {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        audioChunksRef.current = [];
        setIsRecordingVoice(false);
        setRecordingSeconds(0);

        const formData = new FormData();
        const audioFile = new File([audioBlob], `voice-note-${Date.now()}.webm`, {
          type: 'audio/webm',
        });
        formData.append('file', audioFile);
        formData.append('channelId', resolvedChannelId);

        const uploadRes = await fetch('/api/files/upload', {
          method: 'POST',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
          body: formData,
        });

        if (!uploadRes.ok) return;
        const uploadData = await uploadRes.json();
        const uploadedFile = uploadData.file;
        const previewUrl =
          uploadedFile.previewUrl ||
          uploadedFile.downloadUrl ||
          `/api/files/${uploadedFile.id}/preview?token=${encodeURIComponent(token)}`;

        const res = await fetch(`/api/chat/channels/${resolvedChannelId}/messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
          body: JSON.stringify({
            content: '🎤 Voice message',
            voiceNote: {
              durationSeconds: Math.max(1, duration),
              audioUrl: previewUrl,
            },
          }),
        });

        if (res.ok) {
          const data = await res.json();
          setMessages((prev) => {
            if (prev.some((m) => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
          playOutgoingMessageSound();
          setTimeout(() => {
            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 50);
        }
      } catch (err) {
        console.error('[VideoCallModal] Voice note send error:', err);
      }
    };

    recorder.stop();
  };

  if (!activeCall) return null;

  const myId = user?.id || 'usr-admin';
  const sharingParticipant = activeCall.participants.find((p) => p.isScreenSharing);
  const isSelfSharing = Boolean(activeCall.isScreenSharing && screenStream);
  const isPeerSharing = Boolean(
    sharingParticipant && sharingParticipant.id !== myId && remoteStreams[sharingParticipant.id]
  );
  const activeScreenStream = isSelfSharing
    ? screenStream
    : isPeerSharing
    ? remoteStreams[sharingParticipant!.id]
    : null;
  const presenterName = isSelfSharing
    ? 'You'
    : sharingParticipant?.name || 'Remote Participant';

  // Hidden elements for Desktop OS Picture-in-Picture (popup beyond browser) + continuous remote audio
  const backgroundMediaElements = (
    <>
      <canvas ref={pipCanvasRef} width={640} height={360} className="hidden" />
      <video
        ref={pipVideoRef}
        muted
        playsInline
        autoPlay
        className="fixed -bottom-96 -right-96 w-1 h-1 opacity-0 pointer-events-none"
      />
      {Object.entries(remoteStreams).map(([peerId, stream]) => (
        <RemoteAudioPlayer key={`remote-audio-${peerId}`} stream={stream} />
      ))}
    </>
  );

  // ============================================================================
  // MINIMIZED / FLOATING IN-APP PIP WIDGET (BOTTOM RIGHT)
  // Fixed width w-80 sm:w-[340px] so it never enlarges to fill the screen
  // ============================================================================
  if (activeCall.isFloating) {
    const hasActiveLocalVideo =
      !activeCall.isVideoOff && localStream && localStream.getVideoTracks().length > 0;
    const remotePeerWithVideo = activeCall.participants.find(
      (p) =>
        p.id !== myId &&
        p.id !== 'me' &&
        !p.isVideoOff &&
        remoteStreams[p.id] &&
        remoteStreams[p.id].getVideoTracks().length > 0
    );

    return (
      <>
        {backgroundMediaElements}
        <div className="fixed bottom-5 right-5 z-50 w-80 sm:w-[340px] max-w-[calc(100vw-2rem)] bg-[#121417] text-white rounded-3xl shadow-2xl border border-white/15 overflow-hidden flex flex-col font-sans animate-in fade-in slide-in-from-bottom-3">
          <div className="relative h-44 bg-black overflow-hidden flex items-center justify-center">
            {/* Active preview: Screen share, remote camera, local camera, or avatar */}
            {activeScreenStream ? (
              <VideoFeed
                stream={activeScreenStream}
                isMuted={isSelfSharing}
                className="w-full h-full object-contain"
                videoRefCallback={(el) => {
                  activeStageVideoElRef.current = el;
                }}
              />
            ) : remotePeerWithVideo ? (
              <VideoFeed
                stream={remoteStreams[remotePeerWithVideo.id]}
                isMuted={true}
                className="w-full h-full object-cover"
                videoRefCallback={(el) => {
                  activeStageVideoElRef.current = el;
                }}
              />
            ) : hasActiveLocalVideo ? (
              <VideoFeed
                stream={localStream}
                isMuted={true}
                className="w-full h-full object-cover scale-x-[-1]"
                videoRefCallback={(el) => {
                  activeStageVideoElRef.current = el;
                }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-white/70">
                <div className="w-14 h-14 rounded-full bg-brand-600/20 border-2 border-brand-500/40 text-brand-400 flex items-center justify-center font-black text-lg shadow-sm">
                  {(activeCall.title || user?.fullName || 'U').slice(0, 2).toUpperCase()}
                </div>
                <span className="text-[11px] mt-1.5 font-semibold text-white/70">
                  {isOutgoingRinging
                    ? 'Ringing...'
                    : activeCall.isVideoOff
                    ? 'Audio Call Active'
                    : 'Connected'}
                </span>
              </div>
            )}

            {/* Top left duration / ringing badge */}
            <div className="absolute top-2.5 left-2.5 flex items-center space-x-1.5 bg-black/75 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-mono border border-white/10">
              <Radio className="w-3 h-3 text-brand-400 animate-pulse" />
              <span>{isOutgoingRinging ? 'Ringing...' : formatTime(callDuration)}</span>
            </div>

            {/* Top right Expand & Desktop PiP buttons */}
            <div className="absolute top-2.5 right-2.5 flex items-center space-x-1.5">
              <button
                type="button"
                onClick={handleToggleDesktopPiP}
                className={`p-1.5 rounded-full backdrop-blur-md transition-colors ${
                  isDesktopPiPActive
                    ? 'bg-brand-600 text-white'
                    : 'bg-black/70 hover:bg-black text-white/80 hover:text-white'
                }`}
                title="Pop out to Desktop Window (stays visible when browser is minimized)"
              >
                <PictureInPicture2 className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={toggleFloating}
                className="px-2.5 py-1 rounded-full bg-brand-600 hover:bg-brand-500 text-white text-[10px] font-bold flex items-center space-x-1 shadow-md transition-colors"
                title="Return to Full Call Screen"
              >
                <Maximize2 className="w-3 h-3" />
                <span>Expand</span>
              </button>
            </div>
          </div>

          {/* Floating bottom controls */}
          <div className="p-3 bg-[#181b20] flex items-center justify-between gap-2">
            <div
              onClick={toggleFloating}
              className="truncate pr-1 cursor-pointer group flex-1 min-w-0"
              title="Click to expand call"
            >
              <h5 className="text-xs font-bold truncate text-white group-hover:text-brand-400 transition-colors">
                {activeCall.title}
              </h5>
              <span className="text-[10px] text-white/50 block">
                Click to expand • {activeCall.participants.length} in call
              </span>
            </div>

            <div className="flex items-center space-x-1.5 shrink-0">
              <button
                type="button"
                onClick={toggleAudio}
                className={`p-2 rounded-full text-xs transition-colors ${
                  activeCall.isAudioMuted
                    ? 'bg-rose-500 text-white hover:bg-rose-600'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
                title={activeCall.isAudioMuted ? 'Unmute' : 'Mute'}
              >
                {activeCall.isAudioMuted ? (
                  <MicOff className="w-3.5 h-3.5" />
                ) : (
                  <Mic className="w-3.5 h-3.5" />
                )}
              </button>

              <button
                type="button"
                onClick={toggleVideo}
                className={`p-2 rounded-full text-xs transition-colors ${
                  activeCall.isVideoOff
                    ? 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                    : 'bg-brand-600 text-white hover:bg-brand-500'
                }`}
                title={activeCall.isVideoOff ? 'Turn On Camera (Video)' : 'Turn Off Camera'}
              >
                {activeCall.isVideoOff ? (
                  <VideoOff className="w-3.5 h-3.5" />
                ) : (
                  <Video className="w-3.5 h-3.5" />
                )}
              </button>

              <button
                type="button"
                onClick={toggleFloating}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                title="Expand Call Window"
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={endCall}
                className="p-2 rounded-full bg-rose-600 hover:bg-rose-700 text-white transition-colors"
                title="End Call"
              >
                <PhoneOff className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Helper to render a participant tile (used in both regular grid and compact right sidebar during screen share)
  const renderParticipantTile = (participant: any, compactSidebarMode = false) => {
    const isSelf = participant.id === myId || participant.id === 'me';
    const isSpeaking = participant.isSpeaking;

    const peerStream = !isSelf ? remoteStreams[participant.id] : null;
    const selfHasVideo =
      isSelf &&
      !activeCall.isVideoOff &&
      localStream &&
      localStream.getVideoTracks().length > 0;
    const peerHasVideo =
      !isSelf &&
      Boolean(
        peerStream && peerStream.getVideoTracks().length > 0 && !participant.isVideoOff
      );

    return (
      <div
        key={participant.id}
        className={`relative rounded-2xl overflow-hidden bg-[#1c1f26] border-2 transition-all flex flex-col justify-between ${
          isSpeaking
            ? 'border-brand-500 shadow-[0_0_20px_rgba(1,98,252,0.4)]'
            : 'border-white/10'
        } ${
          compactSidebarMode
            ? 'h-32 sm:h-36 w-44 lg:w-full shrink-0'
            : 'min-h-[200px] sm:min-h-[240px]'
        }`}
      >
        {/* Video Tile Background / Camera feed */}
        <div className="absolute inset-0 flex items-center justify-center bg-[#14171d]">
          {isSelf ? (
            selfHasVideo ? (
              <VideoFeed
                stream={localStream}
                isMuted={true}
                className="w-full h-full object-cover scale-x-[-1]"
                videoRefCallback={(el) => {
                  if (!activeScreenStream && activeCall.participants.length === 1) {
                    activeStageVideoElRef.current = el;
                  }
                }}
              />
            ) : (
              <div className="flex flex-col items-center">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.fullName}
                    className={`${
                      compactSidebarMode ? 'w-11 h-11' : 'w-20 h-20'
                    } rounded-full object-cover border-2 border-brand-500/40 shadow-lg`}
                  />
                ) : (
                  <div
                    className={`${
                      compactSidebarMode ? 'w-11 h-11 text-sm' : 'w-20 h-20 text-2xl'
                    } rounded-full bg-brand-600/25 border-2 border-brand-500/40 flex items-center justify-center text-white font-black shadow-lg`}
                  >
                    {(user?.fullName || 'U').slice(0, 2).toUpperCase()}
                  </div>
                )}
                {!compactSidebarMode && (
                  <span className="text-xs font-semibold text-white/60 mt-2.5">
                    {activeCall.isVideoOff ? 'Audio Mode (Camera Off)' : 'Audio Connected'}
                  </span>
                )}
              </div>
            )
          ) : peerHasVideo && peerStream ? (
            <VideoFeed
              stream={peerStream}
              isMuted={true}
              className="w-full h-full object-cover"
              videoRefCallback={(el) => {
                if (!activeScreenStream) {
                  activeStageVideoElRef.current = el;
                }
              }}
            />
          ) : (
            <div className="flex flex-col items-center">
              {participant.avatarUrl ? (
                <img
                  src={participant.avatarUrl}
                  alt={participant.name}
                  className={`${
                    compactSidebarMode ? 'w-11 h-11' : 'w-20 h-20'
                  } rounded-full object-cover border-2 border-white/20 shadow-lg`}
                />
              ) : (
                <div
                  className={`${
                    compactSidebarMode ? 'w-11 h-11 text-sm' : 'w-20 h-20 text-xl'
                  } rounded-full bg-brand-600/30 border-2 border-brand-500/50 flex items-center justify-center text-white font-bold shadow-lg`}
                >
                  {(participant.name || 'TM').slice(0, 2).toUpperCase()}
                </div>
              )}
              {!compactSidebarMode && (
                <span className="text-xs font-semibold text-white/60 mt-2.5">
                  {isOutgoingRinging
                    ? 'Ringing...'
                    : participant.isVideoOff
                    ? 'Audio Mode (Camera Off)'
                    : 'Audio Connected'}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Gradient Overlay for crisp badge visibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 pointer-events-none" />

        {/* Top Status Indicators */}
        <div className="relative z-10 p-2.5 flex items-center justify-between">
          <span className="px-2 py-0.5 rounded-full bg-black/70 backdrop-blur-md text-[10px] font-bold text-white/90 border border-white/10 truncate max-w-[70%]">
            {participant.role || 'Team Member'}
          </span>

          <div className="flex items-center space-x-1">
            {participant.isScreenSharing && (
              <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-r from-[#0162fc] to-[#4b2efa] text-[9px] font-black text-white flex items-center space-x-1">
                <Monitor className="w-2.5 h-2.5" />
                {!compactSidebarMode && <span>Presenting</span>}
              </span>
            )}
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                participant.isMuted
                  ? 'bg-rose-500 text-white'
                  : 'bg-black/70 text-brand-400'
              }`}
            >
              {participant.isMuted ? (
                <MicOff className="w-3 h-3" />
              ) : (
                <Mic className="w-3 h-3" />
              )}
            </div>
          </div>
        </div>

        {/* Bottom Participant Info */}
        <div className="relative z-10 p-2.5 flex items-center justify-between">
          <span className="font-extrabold text-xs text-white truncate">
            {participant.name} {isSelf && '(You)'}
          </span>

          {isSpeaking && (
            <div className="flex items-center space-x-1 bg-brand-500/20 px-2 py-0.5 rounded-full border border-brand-500/40 shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" />
              {!compactSidebarMode && (
                <span className="text-[10px] font-bold text-brand-300">Speaking</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================================================
  // FULL SCREEN / EXPANDED INTERACTIVE CALL MODAL
  // ============================================================================
  return (
    <>
      {backgroundMediaElements}
      <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-0 sm:p-3 lg:p-5 animate-fade-in font-sans">
        <div className="w-full max-w-[1440px] h-full sm:h-[94vh] bg-[#121417] text-white rounded-none sm:rounded-3xl border-0 sm:border border-white/10 shadow-neo-float flex flex-col overflow-hidden relative">
          {/* Top Header Bar */}
          <div className="h-14 px-3.5 sm:px-5 border-b border-white/10 flex items-center justify-between bg-[#181b20] shrink-0">
            <div className="flex items-center space-x-3 min-w-0">
              <div
                className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                  isOutgoingRinging ? 'bg-amber-400 animate-ping' : 'bg-emerald-400 animate-pulse'
                }`}
              />
              <div className="min-w-0">
                <div className="flex items-center space-x-2">
                  <h3 className="font-extrabold text-sm text-white tracking-tight truncate">
                    {activeCall.title}
                  </h3>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-brand-500/15 text-brand-400 border border-brand-500/30 shrink-0">
                    {isOutgoingRinging
                      ? 'Ringing...'
                      : activeCall.isVideoOff
                      ? 'Audio Call'
                      : 'Video Call'}
                  </span>
                </div>
                <span className="text-[11px] text-white/50 font-mono block truncate">
                  {isOutgoingRinging
                    ? 'Waiting for answer...'
                    : `Duration: ${formatTime(callDuration)} • ${
                        activeCall.participants.length
                      } Participant${activeCall.participants.length === 1 ? '' : 's'}`}
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-2 shrink-0">
              {/* Toggle In-Call Chat & File Sharing Drawer */}
              <button
                type="button"
                onClick={() => setShowInCallChat(!showInCallChat)}
                className={`relative px-3 py-1.5 rounded-full text-xs font-bold flex items-center space-x-1.5 transition-colors ${
                  showInCallChat
                    ? 'bg-gradient-to-r from-[#0162fc] to-[#4b2efa] text-white shadow-md'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
                title="Open In-Call Chat & File Sharing"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Chat & Files</span>
                {unreadInCallCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.2 rounded-full bg-rose-500 text-white text-[10px] font-black">
                    {unreadInCallCount}
                  </span>
                )}
              </button>

              {/* Desktop OS Popup Picture-in-Picture (Beyond Browser) */}
              <button
                type="button"
                onClick={handleToggleDesktopPiP}
                className={`px-2.5 py-1.5 rounded-full text-xs font-bold flex items-center space-x-1.5 transition-colors ${
                  isDesktopPiPActive
                    ? 'bg-brand-600 text-white'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
                title="Pop out call to Desktop Window (stays on bottom-right of your desktop even when browser is minimized)"
              >
                <PictureInPicture2 className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Desktop Popup</span>
              </button>

              {/* In-App Minimize to Bottom-Right Corner */}
              <button
                type="button"
                onClick={toggleFloating}
                className="px-2.5 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold flex items-center space-x-1.5 transition-colors"
                title="Minimize call to bottom-right so you can use the app"
              >
                <Minimize2 className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Minimize</span>
              </button>
            </div>
          </div>

          {/* ==========================================================================
              MAIN BODY:
              - When Screen Sharing is active: Google Meet / MS Teams Layout!
                Shared screen grabs the vast majority of the stage on the left/center,
                and participant windows become compact tiles stacked on the right side.
              - When not Screen Sharing: Responsive multi-participant grid.
              - Plus collapsible Live In-Call Chat & File Sharing Drawer on the right.
          ========================================================================== */}
          <div className="flex-1 flex min-h-0 overflow-hidden relative">
            {activeScreenStream ? (
              /* GOOGLE MEET / MS TEAMS SCREEN SHARE LAYOUT */
              <div className="flex-1 flex flex-col lg:flex-row min-w-0 min-h-0 p-3 sm:p-4 gap-3 overflow-hidden">
                {/* Main Stage: Shared Screen (Grabs Majority Screen Area) */}
                <div className="flex-1 min-w-0 min-h-0 bg-black rounded-2xl border border-white/15 relative flex items-center justify-center overflow-hidden shadow-2xl">
                  <VideoFeed
                    stream={activeScreenStream}
                    isMuted={isSelfSharing}
                    className="w-full h-full object-contain"
                    videoRefCallback={(el) => {
                      activeStageVideoElRef.current = el;
                    }}
                  />
                  <div className="absolute top-3 left-3 bg-black/75 backdrop-blur-md px-3 py-1.5 rounded-full text-xs font-bold text-brand-400 flex items-center space-x-2 border border-white/10 shadow-lg">
                    <Monitor className="w-3.5 h-3.5" />
                    <span>{presenterName} is presenting</span>
                  </div>
                </div>

                {/* Right Side Strip: Small Participant Windows (Google Meet / MS Teams style) */}
                <div className="w-full lg:w-56 xl:w-64 shrink-0 flex lg:flex-col gap-2.5 overflow-x-auto lg:overflow-y-auto no-scrollbar">
                  {activeCall.participants.map((participant) =>
                    renderParticipantTile(participant, true)
                  )}
                </div>
              </div>
            ) : (
              /* STANDARD MULTI-PARTICIPANT GRID VIEW */
              <div
                className="flex-1 p-4 grid gap-4 overflow-y-auto min-w-0"
                style={{
                  gridTemplateColumns:
                    activeCall.participants.length === 1
                      ? '1fr'
                      : activeCall.participants.length === 2
                      ? 'repeat(2, minmax(0, 1fr))'
                      : 'repeat(auto-fit, minmax(280px, 1fr))',
                }}
              >
                {activeCall.participants.map((participant) =>
                  renderParticipantTile(participant, false)
                )}
              </div>
            )}

            {/* ==========================================================================
                RIGHT IN-CALL LIVE CHAT & FILE SHARING DRAWER
                Synced with the conversation channel: messages, file/image uploads,
                emojis, reactions, replies, voice notes, and Create Task.
            ========================================================================== */}
            {showInCallChat && (
              <div className="w-full sm:w-96 border-l border-white/10 bg-[#181b20] flex flex-col h-full shrink-0 z-20 shadow-2xl">
                {/* Drawer Header */}
                <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-[#14171c] shrink-0">
                  <div className="flex items-center space-x-2 min-w-0">
                    <MessageSquare className="w-4 h-4 text-brand-400 shrink-0" />
                    <div className="truncate">
                      <h4 className="font-bold text-xs text-white truncate">
                        In-Call Chat & File Sharing
                      </h4>
                      <span className="text-[10px] text-white/50 block truncate">
                        Messages & files sync with conversation history
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setTaskInitialTitle('');
                        setIsCreateTaskOpen(true);
                      }}
                      className="px-2 py-1 rounded-lg bg-brand-600/20 hover:bg-brand-600/30 text-brand-400 border border-brand-500/30 text-[10px] font-bold flex items-center space-x-1 transition-colors"
                      title="Create a task during this call"
                    >
                      <CheckSquare className="w-3 h-3" />
                      <span>+ Task</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowInCallChat(false)}
                      className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10"
                      aria-label="Close chat"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Messages Feed */}
                <div className="flex-1 overflow-y-auto p-3.5 space-y-2.5">
                  {messages.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center p-6 text-white/40">
                      <MessageSquare className="w-8 h-8 mb-2 stroke-[1.5]" />
                      <p className="text-xs font-semibold text-white/60">No messages yet</p>
                      <p className="text-[11px] mt-0.5">
                        Share messages, links, images, or files with everyone on the call.
                      </p>
                    </div>
                  ) : (
                    messages.map((m) => {
                      const isMe = m.senderId === user?.id;
                      return (
                        <div
                          key={m.id}
                          onMouseEnter={() => setHoveredMsgId(m.id)}
                          onMouseLeave={() => setHoveredMsgId(null)}
                          className={`group relative flex flex-col ${
                            isMe ? 'items-end' : 'items-start'
                          }`}
                        >
                          {/* Quick Reaction / Reply / Task Hover Bar */}
                          {hoveredMsgId === m.id && !m.isDeleted && (
                            <div
                              className={`flex items-center space-x-0.5 mb-1 px-1.5 py-0.5 rounded-full bg-[#22262e] border border-white/10 shadow-md text-[11px] z-10 ${
                                isMe ? 'self-end' : 'self-start'
                              }`}
                            >
                              {['👍', '❤️', '🔥', '✅'].map((emoji) => (
                                <button
                                  key={emoji}
                                  type="button"
                                  onClick={() => handleToggleReaction(m.id, emoji)}
                                  className="px-1 hover:scale-125 transition-transform"
                                >
                                  {emoji}
                                </button>
                              ))}
                              <button
                                type="button"
                                onClick={() => setReplyingTo(m)}
                                className="p-1 text-white/60 hover:text-white"
                                title="Reply"
                              >
                                <Reply className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => openForwardMessage(m)}
                                className="p-1 text-white/60 hover:text-white"
                                title="Forward"
                              >
                                <Forward className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(m.content || '');
                                  setCopiedMsgId(m.id);
                                  setTimeout(() => setCopiedMsgId(null), 1500);
                                }}
                                className="p-1 text-white/60 hover:text-white"
                                title="Copy text"
                              >
                                {copiedMsgId === m.id ? (
                                  <Check className="w-3 h-3 text-emerald-400" />
                                ) : (
                                  <Copy className="w-3 h-3" />
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setTaskInitialTitle(m.content || '');
                                  setIsCreateTaskOpen(true);
                                }}
                                className="p-1 text-white/60 hover:text-brand-400"
                                title="Create Task from message"
                              >
                                <CheckSquare className="w-3 h-3" />
                              </button>
                            </div>
                          )}

                          <div
                            className={`max-w-[86%] rounded-2xl px-3 py-2 text-xs ${
                              isMe
                                ? 'bg-brand-600 text-white rounded-br-xs'
                                : 'bg-white/10 text-white/95 border border-white/10 rounded-bl-xs'
                            }`}
                          >
                            {!isMe && (
                              <div className="font-bold text-[10px] text-brand-400 mb-0.5">
                                {m.senderName}
                              </div>
                            )}

                            {/* Forwarded label */}
                            {m.forwardedFromId && (
                              <div className="mb-1 flex items-center space-x-1 text-[10px] italic opacity-70">
                                <Forward className="w-2.5 h-2.5" />
                                <span>Forwarded</span>
                              </div>
                            )}

                            {/* Reply Quote */}
                            {m.replyTo && (
                              <div className="mb-1.5 pl-2 border-l-2 border-white/40 text-[10px] opacity-80 truncate">
                                <span className="font-bold">{m.replyTo.senderName}: </span>
                                {m.replyTo.content}
                              </div>
                            )}

                            {/* Voice Note */}
                            {m.voiceNote ? (
                              <div className="my-1">
                                <VoiceNotePlayer
                                  audioUrl={m.voiceNote.audioUrl}
                                  durationSeconds={m.voiceNote.durationSeconds}
                                  isOwnMessage={isMe}
                                />
                              </div>
                            ) : (
                              m.content && (
                                <div className="whitespace-pre-wrap break-words leading-relaxed">
                                  <LinkifiedText text={m.content} />
                                </div>
                              )
                            )}

                            {/* Attachments (Images & Files) */}
                            {m.attachments && m.attachments.length > 0 && (
                              <div className="mt-2 space-y-1.5">
                                {m.attachments.map((att, i) => {
                                  const isImg = isImageFile(att.fileName, att.mimeType);
                                  const fileUrl = att.url
                                    ? att.url.includes('token=')
                                      ? att.url
                                      : `${att.url}${att.url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token || '')}`
                                    : `/api/files/${att.fileId}/download?token=${encodeURIComponent(token || '')}`;

                                  if (isImg) {
                                    const downloadHref = att.fileId
                                      ? `/api/files/${att.fileId}/download?token=${encodeURIComponent(token || '')}`
                                      : fileUrl;
                                    return (
                                      <div key={i} className="relative group rounded-xl overflow-hidden border border-white/15">
                                        <a href={fileUrl} target="_blank" rel="noreferrer" title="Open preview" className="block">
                                          <img
                                            src={fileUrl}
                                            alt={att.fileName}
                                            className="max-h-44 w-auto object-cover"
                                          />
                                        </a>
                                        <a
                                          href={downloadHref}
                                          download={att.fileName}
                                          title="Download"
                                          className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                                        >
                                          <Download className="w-3.5 h-3.5" />
                                        </a>
                                      </div>
                                    );
                                  }

                                  return (
                                    <a
                                      key={i}
                                      href={fileUrl}
                                      download={att.fileName}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="flex items-center justify-between p-2 rounded-xl bg-black/30 hover:bg-black/50 border border-white/10 text-[11px] transition-colors"
                                    >
                                      <div className="flex items-center space-x-2 min-w-0 pr-2">
                                        <FileText className="w-4 h-4 text-brand-400 shrink-0" />
                                        <span className="truncate font-medium">{att.fileName}</span>
                                      </div>
                                      <Download className="w-3.5 h-3.5 text-white/70 shrink-0" />
                                    </a>
                                  );
                                })}
                              </div>
                            )}

                            <div className="text-[9px] opacity-60 text-right mt-1">
                              {formatCallMsgTime(m.createdAt)}
                            </div>
                          </div>

                          {/* Reactions Row */}
                          {m.reactions && m.reactions.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {m.reactions.map((r) => (
                                <button
                                  key={r.emoji}
                                  type="button"
                                  onClick={() => handleToggleReaction(m.id, r.emoji)}
                                  className="px-1.5 py-0.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-[10px] flex items-center space-x-1"
                                >
                                  <span>{r.emoji}</span>
                                  <span className="font-bold">{r.count}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Reply Banner */}
                {replyingTo && (
                  <div className="px-3.5 py-1.5 bg-white/5 border-t border-white/10 flex items-center justify-between text-[11px]">
                    <div className="truncate pr-2">
                      <span className="text-brand-400 font-bold">
                        Replying to {replyingTo.senderName}:{' '}
                      </span>
                      <span className="text-white/70">{replyingTo.content}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setReplyingTo(null)}
                      className="text-white/50 hover:text-white"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {/* Staged File Attachments Preview */}
                {stagedAttachments.length > 0 && (
                  <div className="px-3.5 py-2 bg-white/5 border-t border-white/10 flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                    {stagedAttachments.map((att, idx) => (
                      <div
                        key={idx}
                        className="flex items-center space-x-1.5 px-2.5 py-1 rounded-xl bg-white/10 border border-white/15 text-[11px] text-white"
                      >
                        {isImageFile(att.fileName, att.mimeType) && att.url ? (
                          <img
                            src={
                              att.url.includes('token=')
                                ? att.url
                                : `${att.url}${att.url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token || '')}`
                            }
                            alt={att.fileName}
                            className="w-9 h-9 rounded-md object-cover shrink-0"
                          />
                        ) : (
                          <FileText className="w-3 h-3 text-brand-400 shrink-0" />
                        )}
                        <span className="truncate max-w-[140px]">{att.fileName}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setStagedAttachments((prev) => prev.filter((_, i) => i !== idx))
                          }
                          className="text-white/60 hover:text-rose-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Chat & File Sharing Input Composer */}
                <div className="p-3 border-t border-white/10 bg-[#14171c] relative">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => e.target.files && uploadInCallFiles(e.target.files)}
                  />

                  {isEmojiOpen && (
                    <div className="absolute bottom-14 left-3 z-30">
                      <EmojiPickerPopover
                        onSelect={(emoji) => {
                          setChatInput((prev) => prev + emoji);
                          setIsEmojiOpen(false);
                        }}
                        onClose={() => setIsEmojiOpen(false)}
                      />
                    </div>
                  )}

                  {isRecordingVoice ? (
                    <div className="flex items-center justify-between px-3 py-2 rounded-2xl bg-rose-500/15 border border-rose-500/30">
                      <div className="flex items-center space-x-2">
                        <span className="w-2 h-2 rounded-full bg-rose-500 animate-ping" />
                        <span className="text-xs font-mono font-bold text-rose-300">
                          Recording {formatTime(recordingSeconds)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1.5">
                        <button
                          type="button"
                          onClick={cancelVoiceNoteRecording}
                          className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white"
                          title="Cancel"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={sendVoiceNoteRecording}
                          className="px-3 py-1 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold"
                        >
                          Send
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSendInCallMessage} className="flex items-center space-x-1.5">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploadingFile}
                        className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors shrink-0"
                        title="Share File or Image"
                      >
                        {isUploadingFile ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-400" />
                        ) : (
                          <Paperclip className="w-3.5 h-3.5" />
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => setIsEmojiOpen(!isEmojiOpen)}
                        className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors shrink-0"
                        title="Add Emoji"
                      >
                        <Smile className="w-3.5 h-3.5" />
                      </button>

                      <input
                        type="text"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onPaste={(e) => {
                          // a copied screenshot / image / file pasted into the chat box is attached
                          const pasted = Array.from(e.clipboardData?.files || []).filter((f) => f.size > 0);
                          if (pasted.length > 0) {
                            e.preventDefault();
                            uploadInCallFiles(pasted);
                          }
                        }}
                        placeholder="Message or share file..."
                        className="flex-1 min-w-0 px-3 py-2 rounded-2xl bg-black/40 border border-white/10 text-xs text-white placeholder-white/40 focus:outline-none focus:border-brand-500"
                      />

                      {chatInput.trim() || stagedAttachments.length > 0 ? (
                        <button
                          type="submit"
                          disabled={isSendingChat}
                          className="p-2 rounded-full bg-brand-600 hover:bg-brand-500 text-white transition-colors shrink-0"
                          title="Send Message"
                        >
                          <Send className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={startVoiceNoteRecording}
                          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors shrink-0"
                          title="Record Voice Note"
                        >
                          <Mic className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </form>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Bottom Call Controls Capsule */}
          <div className="h-16 sm:h-20 px-3 sm:px-6 border-t border-white/10 bg-[#181b20] flex items-center justify-center space-x-2 sm:space-x-3 shrink-0">
            {/* Mute Audio */}
            <button
              type="button"
              onClick={toggleAudio}
              className={`p-3 sm:px-4 sm:py-2.5 rounded-full font-bold text-xs flex items-center space-x-2 transition-all shadow-neo ${
                activeCall.isAudioMuted
                  ? 'bg-rose-500 text-white hover:bg-rose-600'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              title={activeCall.isAudioMuted ? 'Unmute Microphone' : 'Mute Microphone'}
            >
              {activeCall.isAudioMuted ? (
                <MicOff className="w-4 h-4" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">
                {activeCall.isAudioMuted ? 'Unmute' : 'Mute'}
              </span>
            </button>

            {/* Video Toggle (Allows upgrading Audio Call -> Video Call at any time) */}
            <button
              type="button"
              onClick={toggleVideo}
              className={`p-3 sm:px-4 sm:py-2.5 rounded-full font-bold text-xs flex items-center space-x-2 transition-all shadow-neo ${
                activeCall.isVideoOff
                  ? 'bg-white/10 text-white hover:bg-white/20'
                  : 'bg-brand-600 text-white hover:bg-brand-500'
              }`}
              title={
                activeCall.isVideoOff
                  ? 'Turn On Camera (Switch to Video Call)'
                  : 'Turn Off Camera'
              }
            >
              {activeCall.isVideoOff ? (
                <VideoOff className="w-4 h-4" />
              ) : (
                <Video className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">
                {activeCall.isVideoOff ? 'Start Video' : 'Stop Video'}
              </span>
            </button>

            {/* Screen Share */}
            <button
              type="button"
              onClick={toggleScreenShare}
              className={`p-3 sm:px-4 sm:py-2.5 rounded-full font-bold text-xs flex items-center space-x-2 transition-all shadow-neo ${
                activeCall.isScreenSharing
                  ? 'bg-gradient-to-r from-[#0162fc] to-[#4b2efa] text-white font-extrabold shadow-pulse'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              title={activeCall.isScreenSharing ? 'Stop Sharing Screen' : 'Share Screen'}
            >
              <Monitor className="w-4 h-4" />
              <span className="hidden sm:inline">
                {activeCall.isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
              </span>
            </button>

            {/* In-Call Chat & File Sharing Toggle */}
            <button
              type="button"
              onClick={() => setShowInCallChat(!showInCallChat)}
              className={`relative p-3 sm:px-4 sm:py-2.5 rounded-full font-bold text-xs flex items-center space-x-2 transition-all shadow-neo ${
                showInCallChat
                  ? 'bg-gradient-to-r from-[#0162fc] to-[#4b2efa] text-white'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              title="In-Call Chat & File Sharing"
            >
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Chat & Files</span>
              {unreadInCallCount > 0 && (
                <span className="px-1.5 py-0.2 rounded-full bg-rose-500 text-white text-[10px] font-black">
                  {unreadInCallCount}
                </span>
              )}
            </button>

            {/* Minimize In-App */}
            <button
              type="button"
              onClick={toggleFloating}
              className="p-3 sm:px-4 sm:py-2.5 rounded-full font-bold text-xs bg-white/10 hover:bg-white/20 text-white flex items-center space-x-2 transition-all"
              title="Minimize to corner so you can use the app during call"
            >
              <Minimize2 className="w-4 h-4" />
              <span className="hidden lg:inline">Minimize</span>
            </button>

            {/* Leave Call */}
            <button
              type="button"
              onClick={endCall}
              className="px-4 sm:px-6 py-2.5 rounded-full font-extrabold text-xs bg-rose-600 hover:bg-rose-700 text-white flex items-center space-x-2 shadow-neo transition-transform hover:scale-105"
              title="End Call"
            >
              <PhoneOff className="w-4 h-4" />
              <span className="hidden sm:inline">Leave Call</span>
            </button>
          </div>
        </div>
      </div>

      {/* Forward Message Modal inside Call */}
      <ForwardMessageModal
        isOpen={Boolean(forwardingMessage)}
        onClose={() => setForwardingMessage(null)}
        message={forwardingMessage}
        channels={forwardChannels}
        currentUser={user}
        onForward={handleForwardMessage}
      />

      {/* Create Task Modal inside Call */}
      <CreateTaskModal
        isOpen={isCreateTaskOpen}
        onClose={() => setIsCreateTaskOpen(false)}
        projectId={channelProjectId}
        initialTitle={taskInitialTitle}
        onTaskCreated={(createdTask) => {
          setIsCreateTaskOpen(false);
          if (resolvedChannelId && token && createdTask?.title) {
            fetch(`/api/chat/channels/${resolvedChannelId}/messages`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
              },
              body: JSON.stringify({
                content: `✅ Created task: **${createdTask.title}**`,
                linkedTaskId: createdTask.id,
              }),
            }).catch(() => {});
          }
        }}
      />
    </>
  );
};
